import React, { useState, useEffect } from "react";
import { Box, Grid, Stack, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import AvatarSection from "./AvatarSection";
import ProfileInfo from "./ProfileInfo";
import PointsSection from "./PointsSection";
import DeleteUserDialog from "./DeleteUserDialog";
import ChangePassword from "../ChangePassword/ChangePassword";
import InterestsDialog from "./InterestsDialog";
import InterestsSection from "./InterestsSection";
import { API_URL, popupTimer } from "../../consts";
import { storeProfileSafely } from "../../utils/authUtils";

const Profile = () => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [searchTags, setSearchTags] = useState([]);
  const [data, setData] = useState({
    company_name: "",
    email: "",
    email_preference: {},
    first_name: "",
    last_name: "",
    plan: "",
    points: 0,
    profile_image_url: "",
    oldPassword: "",
    newPassword: "",
    password: "",
  });
  const [dataOriginal, setDataOriginal] = useState({ ...data });
  const [deleteOpen, setDeleteUserDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState({ show: false, type: '', title: '', message: '' });
  const [cancelling, setCancelling] = useState(false);
  const [interestsDialogOpen, setInterestsDialogOpen] = useState(false);
  const [isInterestsRequired, setIsInterestsRequired] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageFile, setPreviewImageFile] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewImageArrayBuffer, setPreviewImageArrayBuffer] = useState(null);
  const [uploading, setUploading] = useState(false);
  const authorized = useSelector((state) => state.authorized);
  const token = useSelector((state) => state?.token);

  const handleFormChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (authorized) {
      axios.get(`${API_URL}/profile`)
        .then((res) => {
          const profileData = res.data?.profile;
          setData(profileData);
          setDataOriginal(profileData);
          setSearchTags(profileData?.tags);
        })
        .catch((err) => {
          console.error("Error fetching profile", err);
          // setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
        });
    }
  }, [authorized]);

  const imageChange = (e) => {
    const image = e.target.files[0];
    if (!image) return;

    // Validate file size (max 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (image.size > MAX_FILE_SIZE) {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "Image size exceeds 10MB limit",
          type: "error",
        },
      });
      setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
      return;
    }

    // Validate file type
    if (!image.type.startsWith('image/')) {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "Please select a valid image file",
          type: "error",
        },
      });
      setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(image);
    setPreviewImageUrl(previewUrl);
    setPreviewImageFile(image);

    // Read file as ArrayBuffer for later upload
    const reader = new FileReader();
    
    reader.onload = () => {
      setPreviewImageArrayBuffer(reader.result);
      setImagePreviewOpen(true);
    };

    reader.onerror = () => {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "File reading failed. Please try another image.",
          type: "error",
        },
      });
      setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
    };

    // Read file as ArrayBuffer for binary upload
    reader.readAsArrayBuffer(image);
  };

  const handleUploadImage = async () => {
    if (!previewImageFile || !previewImageArrayBuffer) return;

    setUploading(true);
    try {
      // Upload the image file as binary data
      const response = await axios.post(
        `${API_URL}/profile/upload-image`,
        previewImageArrayBuffer, // ArrayBuffer from readAsArrayBuffer
        {
          withCredentials: true, // SECURITY: Cookies are sent automatically
          headers: {
            'Content-Type': previewImageFile.type, // Set the content type from the file
          },
        }
      );

      if (response.data?.profile_image_url) {
        // Clean up preview
        if (previewImageUrl) {
          URL.revokeObjectURL(previewImageUrl);
        }
        setImagePreviewOpen(false);
        setPreviewImageFile(null);
        setPreviewImageUrl(null);
        setPreviewImageArrayBuffer(null);
        
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "Profile image updated successfully!",
            type: "success",
          },
        });
        
        // Hard refresh the page after a short delay to show success message
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (err) {
      console.error("Error uploading profile image", err);
      if (err?.response?.status === 401) {
        dispatch({ type: "SET_UNAUTHORIZED" });
        return;
      }
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: err.response?.data?.error || "Failed to upload profile image",
          type: "error",
        },
      });
      setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
      setUploading(false);
    }
  };

  const handleCancelPreview = () => {
    // Clean up preview URL
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
    }
    setImagePreviewOpen(false);
    setPreviewImageFile(null);
    setPreviewImageUrl(null);
    setPreviewImageArrayBuffer(null);
  };

  const updateProfile = (fieldName, fieldValue) => {
    axios.put(`${API_URL}/profile`, { ...data, [fieldName]: fieldValue })
      .then((res) => {
        if (res.data?.profile) {
          setData(res.data.profile);
          setDataOriginal(res.data.profile);
        }
      })
      .catch((err) => {
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: err.response?.data?.error || "Failed to update profile",
            type: "error",
          },
        });
        setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
      });
  };

  const handleSaveInterests = (interests) => {
    // SECURITY: Cookies are sent automatically, no Authorization header needed
    axios.put(`${API_URL}/profile`, { ...data, most_interested: interests }, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (res.data?.profile) {
          const updatedProfile = res.data.profile;
          setData(updatedProfile);
          setDataOriginal(updatedProfile);
          // Also update Redux state
          dispatch({ type: "SET_USER_DATA", payload: updatedProfile });
          // Store only non-sensitive profile data
          storeProfileSafely(updatedProfile);
        }
        setIsInterestsRequired(false);
        setInterestsDialogOpen(false);
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "Your interests have been saved successfully!",
            type: "success",
          },
        });
        setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
      })
      .catch((err) => {
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: err.response?.data?.error || "Failed to update interests",
            type: "error",
          },
        });
        setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
      });
  };

  const deleteUser = () => {
    axios.delete(`${API_URL}/profile/delete-user`)
      .then(() => {
        setDeleteUserDialog(false);
        dispatch({ type: "SET_UNAUTHORIZED" });
      })
      .catch((err) => {
        console.error("Error deleting user", err);
        setDeleteUserDialog(false);
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: err?.response?.data?.error || "Failed to delete account. Please try again.",
            type: "error",
          },
        });
        setTimeout(() => dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } }), popupTimer);
      });
  };

  const shouldShowSubscriptionMessage = () => {
    if (!data.subscription_status || !data.plan_id || !data.plan_expiration_date) {
      return false;
    }
    
    // Check if subscription is cancelled but plan is still active
    if (data.subscription_status === 'cancelled') {
      const expirationDate = new Date(data.plan_expiration_date);
      const currentDate = new Date();
      return expirationDate > currentDate;
    }
    
    return false;
  };

  const formatExpirationDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleCancelSubscription = async () => {
    setCancelDialog({
      show: true,
      type: 'confirm',
      title: 'Cancel Subscription',
      message: 'Are you sure you want to cancel your subscription? This action cannot be undone and you will lose access to premium features at the end of your billing period.'
    });
  };

  const confirmCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await axios.post(
        `${API_URL}/plans/cancel-subscription`,
        {},
        {
          withCredentials: true, // SECURITY: Cookies are sent automatically
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      
      setCancelDialog({
        show: true,
        type: 'success',
        title: 'Subscription Cancelled',
        message: 'Your subscription has been cancelled successfully. You will retain access to premium features until the end of your current billing period.'
      });
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error("❌ Error cancelling subscription:", error);
      const serverError = error.response?.data?.error;
      setCancelDialog({
        show: true,
        type: 'error',
        title: 'Cancellation Error',
        message: serverError || 'There was an error cancelling your subscription. Please contact our support team for assistance.'
      });
    } finally {
      setCancelling(false);
    }
  };

  const closeCancelDialog = () => {
    setCancelDialog({ show: false, type: '', title: '', message: '' });
  };

  if (!authorized)
    return (
      <Box>
        <h2>Profile</h2>
        <p>Please log in to view your profile.</p>
        <button onClick={() => (window.location.href = "/login")}>Login</button>
      </Box>
    );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Grid container spacing={3}>
        {/* Left Column - Profile Info */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <AvatarSection 
              data={data} 
              imageChange={imageChange}
              shouldShowSubscriptionMessage={shouldShowSubscriptionMessage()}
              formatExpirationDate={formatExpirationDate}
              onCancelSubscription={handleCancelSubscription}
              cancelling={cancelling}
            />
            <ProfileInfo
              data={data}
              dataOriginal={dataOriginal}
              handleFormChange={handleFormChange}
              updateProfile={updateProfile}
            />
            <InterestsSection
              interests={data?.most_interested || []}
              onEdit={() => {
                setIsInterestsRequired(false);
                setInterestsDialogOpen(true);
              }}
            />
          </Stack>
        </Grid>
        
        {/* Right Column - Points & Actions */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <PointsSection />
          </Stack>
        </Grid>
      </Grid>
      
      <DeleteUserDialog
        deleteOpen={deleteOpen}
        setDeleteUserDialog={setDeleteUserDialog}
        deleteUser={deleteUser}
      />
      <ChangePassword open={showModal} handleClose={() => setShowModal(false)} />
      
      {/* Interests Dialog */}
      <InterestsDialog
        open={interestsDialogOpen}
        onClose={() => {
          setInterestsDialogOpen(false);
        }}
        onSave={handleSaveInterests}
        initialInterests={data?.most_interested || []}
        isRequired={isInterestsRequired}
      />
      
      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialog.show} onClose={closeCancelDialog}>
        <DialogTitle>{cancelDialog.title}</DialogTitle>
        <DialogContent>
          <p>{cancelDialog.message}</p>
        </DialogContent>
        <DialogActions>
          {cancelDialog.type === 'confirm' ? (
            <>
              <Button onClick={closeCancelDialog}>Cancel</Button>
              <Button 
                onClick={() => {
                  closeCancelDialog();
                  confirmCancelSubscription();
                }}
                disabled={cancelling}
                color="error"
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel Subscription'}
              </Button>
            </>
          ) : (
            <Button onClick={closeCancelDialog}>OK</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog 
        open={imagePreviewOpen} 
        onClose={handleCancelPreview}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Preview Profile Image</DialogTitle>
        <DialogContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2,
            py: 2
          }}>
            {previewImageUrl && (
              <Box
                component="img"
                src={previewImageUrl}
                alt="Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: 2,
                  objectFit: 'contain',
                  border: '1px solid #e0e0e0'
                }}
              />
            )}
            {previewImageFile && (
              <Typography variant="body2" color="textSecondary">
                File: {previewImageFile.name} ({(previewImageFile.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelPreview} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUploadImage} 
            variant="contained"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;
